import { useState, useCallback, useEffect, useRef, type RefObject } from "react";
import { observer } from "@legendapp/state/react";
import ReactPlayer from "react-player";
import type { VideoActivity } from "../types";
import { isPaused$, playerRef, videoDuration$ } from "../state/videoState";
import { useVideoPlayer } from "../hooks/useVideoPlayer";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";
import FeedReelSkeleton from "../components/FeedReelSkeleton";
// Cloudflare Stream HLS URL — shared helper (single source of truth for the
// customer subdomain; mirrors the lessons-track player + iOS CloudflarePlayer).
import {
  cloudflareHlsUrl,
  cloudflareThumbnailUrl,
  cloudflareFileConfig,
} from "@/lib/cloudflare";

type Source = "youtube" | "cloudflare";

interface VideoPlayerProps {
  videoActivity: VideoActivity;
  /** Lesson the clip belongs to — used for listening-time interaction context. */
  lessonId?: number;
  /** Chapter the clip belongs to — used for listening-time interaction context. */
  chapterId?: number;
  /** Fired when no playable source remains (after the YouTube→Cloudflare fallback fails). */
  onUnrecoverableError?: () => void;
  /**
   * When true, paint the Cloudflare thumbnail (sampled at the clip's segment
   * start) BEHIND the player so the first frame shows immediately instead of a
   * black box + spinner — matching iOS CloudflarePlayer.setupThumbnail. Opt-in
   * so the feed (which doesn't pass it) keeps its current behaviour unchanged;
   * the lessons / Flight School mount (VideoPage) turns it on.
   */
  showPoster?: boolean;
  /** When false, watching does NOT accrue listen-time toward the daily goal.
   *  Placement-test videos pass false (iOS doesn't track placement watch time);
   *  the feed/lessons leave it default (true). */
  countListenTime?: boolean;
  /**
   * Mutes the underlying player. The inline Flight School video prompt sets this
   * so the Cloudflare clip can muted-autoplay inline (Chrome blocks UNMUTED
   * autoplay). Default false preserves the feed/lessons (sound-on) behaviour.
   * Also enables `playsinline` so iOS Safari plays it in place rather than going
   * fullscreen.
   */
  muted?: boolean;
  /**
   * Reports the TRUE decoded aspect ratio (width/height) of the playing video
   * once known — read from the Cloudflare `<video>`'s `videoWidth/videoHeight`,
   * mirroring iOS `presentationSize` (CloudflarePlayer.swift). The backend
   * `aspectRatio` STRING is unreliable (portrait clips mislabeled "16:9"), so
   * the layout uses this real value to decide portrait-vs-landscape. Only fires
   * for the Cloudflare path (a `<video>` element); the YouTube iframe exposes no
   * dimensions, so callers fall back to the metadata string there.
   */
  onVideoAspect?: (widthOverHeight: number) => void;
  /**
   * Reports the player kind actually in use ("youtube" | "cloudflare"). YouTube
   * draws its own centre play button when paused, so the caller hides its own
   * centre indicator for YouTube to avoid two overlapping glyphs.
   */
  onSourceResolved?: (source: Source) => void;
}

const VideoPlayer = observer(function VideoPlayer({
  videoActivity,
  lessonId,
  chapterId,
  onUnrecoverableError,
  showPoster = false,
  countListenTime = true,
  muted = false,
  onVideoAspect,
  onSourceResolved,
}: VideoPlayerProps) {
  const [readyKey, setReadyKey] = useState<string | null>(null);

  // Detect the REAL video aspect from the Cloudflare <video> (iOS presentationSize
  // parity) so a mislabeled `aspectRatio` string can't force the wrong layout.
  const rootRef = useRef<HTMLDivElement>(null);
  const onVideoAspectRef = useRef(onVideoAspect);
  onVideoAspectRef.current = onVideoAspect;
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let video: HTMLVideoElement | null = null;
    let lastRatio = 0;
    const report = () => {
      if (video && video.videoWidth > 0 && video.videoHeight > 0) {
        const ratio = video.videoWidth / video.videoHeight;
        // HLS rendition switches fire `resize` with the same aspect — only
        // report when the ratio actually changes, to avoid waking the parent.
        if (Math.abs(ratio - lastRatio) < 0.001) return;
        lastRatio = ratio;
        onVideoAspectRef.current?.(ratio);
      }
    };
    const attach = () => {
      const found = root.querySelector("video");
      if (!found || found === video) return !!video;
      // A NEW <video> element (clip change / source fallback): detach the old
      // one's listeners and reset lastRatio so this element reports at least once
      // even if its aspect matches the previous clip's (the parent cleared
      // realAspectW on the lesson change and is waiting for a fresh report).
      if (video) {
        video.removeEventListener("loadedmetadata", report);
        video.removeEventListener("resize", report);
      }
      video = found;
      lastRatio = 0;
      report();
      video.addEventListener("loadedmetadata", report);
      video.addEventListener("resize", report);
      return true;
    };
    attach();
    // The HLS <video> can mount after this effect runs; watch for it.
    const obs = new MutationObserver(attach);
    obs.observe(root, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      video?.removeEventListener("loadedmetadata", report);
      video?.removeEventListener("resize", report);
    };
    // Re-key on lessonId so this re-attaches and re-reports per lesson even when
    // two clips share the SAME <video> element (same Cloudflare video ID): the
    // parent cleared realAspectW for the new lesson and needs a fresh report.
  }, [lessonId]);

  // The player's REAL playing state, driven by onPlay/onPause. After a blocked
  // unmuted autoplay the player is paused but isPaused$ still reads false (we
  // requested play, no onPlay/onPause ever fired), so a tap that merely toggles
  // isPaused$ flips it to true → ReactPlayer's `playing` goes false → no-op, and
  // the first tap does nothing. Deciding the tap off real state instead breaks
  // that desync: not-actually-playing → request play, actually-playing → pause.
  const actuallyPlayingRef = useRef(false);

  const {
    url: youtubeUrl,
    playing,
    playbackRate,
    handleProgress: rawHandleProgress,
    handleDuration,
    lessonStartMs,
  } = useVideoPlayer(videoActivity, { lessonId, chapterId, countListenTime });

  // Fire video_player_viewed once per mount — iOS useTrackVideoPlayerViewed only
  // fires for the feed + lessons-track players, NOT the embedded FS video bubble
  // or the placement video. Those mount this shared player with
  // countListenTime={false}, so gate the event on countListenTime to avoid
  // inflating the metric (iOS FeedNew.tsx:1115 / VideoNew.tsx:250 are the only
  // callers).
  const { trackVideoPlayerViewed } = useTrackFeedEvents();
  useEffect(() => {
    if (countListenTime) trackVideoPlayerViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const youtubeId = videoActivity.youtubeId;
  const cloudflareId = videoActivity.cloudflareId;

  // Initial source: backend preference, else YouTube if present, else Cloudflare.
  const initialSource: Source =
    videoActivity.player === "cloudflare" && cloudflareId
      ? "cloudflare"
      : youtubeId
      ? "youtube"
      : cloudflareId
      ? "cloudflare"
      : "youtube";
  const [source, setSource] = useState<Source>(initialSource);

  // Report the resolved player kind so the caller can hide its own centre
  // play/pause indicator for YouTube (which draws its own).
  const onSourceResolvedRef = useRef(onSourceResolved);
  onSourceResolvedRef.current = onSourceResolved;
  // The resolved source is reported imperatively where it changes — initial /
  // clip-change from the reset effect (with the fresh initialSource), and the
  // youtube<->cloudflare fallback from handleError — rather than via a
  // state-watching effect ("notify parent on state change" anti-pattern).

  // One-shot fallback guard: without it, when both IDs exist and both fail,
  // handleError flips youtube<->cloudflare forever instead of surfacing the
  // unrecoverable error (same guard as components/Video/VideoPlayer.tsx).
  const fallbackAttemptedRef = useRef(false);

  // Reset the source whenever the underlying video changes, and report the FRESH
  // resolved source for the new clip immediately (the parent cleared its cached
  // value for this lesson). Reporting initialSource here — not the `source`
  // state — avoids briefly emitting the previous clip's source on a cross-source
  // swipe.
  useEffect(() => {
    setSource(initialSource);
    setReadyKey(null);
    fallbackAttemptedRef.current = false;
    onSourceResolvedRef.current?.(initialSource);
    // Keyed on lessonId too, so two clips sharing the same video ID still reset
    // + re-report (the parent cleared its cached source for the new lesson).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeId, cloudflareId, lessonId]);

  const url =
    source === "cloudflare" && cloudflareId ? cloudflareHlsUrl(cloudflareId) : youtubeUrl;

  // On a playback error, try the OTHER source once; if neither works, surface
  // the unrecoverable error so the feed can drop this chapter (iOS handleVideoError).
  const handleError = useCallback(() => {
    if (!fallbackAttemptedRef.current) {
      if (source === "youtube" && cloudflareId) {
        fallbackAttemptedRef.current = true;
        setSource("cloudflare");
        onSourceResolvedRef.current?.("cloudflare");
        setReadyKey(null);
        return;
      }
      if (source === "cloudflare" && youtubeId) {
        fallbackAttemptedRef.current = true;
        setSource("youtube");
        onSourceResolvedRef.current?.("youtube");
        setReadyKey(null);
        return;
      }
    }
    onUnrecoverableError?.();
  }, [source, cloudflareId, youtubeId, onUnrecoverableError]);

  const expectedKey = `${url}@${lessonStartMs}`;
  const isReady = readyKey === expectedKey;

  const handleProgress = useCallback(
    (state: { played: number; loaded: number; playedSeconds: number }) => {
      rawHandleProgress(state);
      if (readyKey === expectedKey) return;
      const duration = videoDuration$.peek();
      if (duration <= 0) return;
      const currentMs = state.played * duration * 1000;
      if (Math.abs(currentMs - lessonStartMs) < 500) {
        setReadyKey(expectedKey);
      }
    },
    [rawHandleProgress, readyKey, expectedKey, lessonStartMs],
  );

  // Cloudflare thumbnail poster (lessons / Flight School only). Sampled at the
  // clip's segment start so the still matches the first played frame; sits
  // behind the player and fades out once playback is ready — iOS parity for the
  // "zero black flash" first frame. YouTube clips already render YouTube's own
  // poster frame, so the poster is Cloudflare-only.
  const posterUrl =
    showPoster && source === "cloudflare" && cloudflareId
      ? cloudflareThumbnailUrl(cloudflareId, lessonStartMs)
      : null;

  return (
    <div ref={rootRef} className="w-full h-full relative">
      {posterUrl && (
        <img
          src={posterUrl}
          alt=""
          aria-hidden
          className={`absolute inset-0 z-0 w-full h-full object-contain bg-black transition-opacity duration-300 ${
            isReady ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        />
      )}
      {!isReady && (
        // Loading treatment: the SAME skeleton the feed uses (FeedReelSkeleton)
        // — soft pulsing fill + centered spinner — so lesson/placement loading
        // matches the feed exactly instead of a one-off variant. No real media
        // preloading (iOS is metadata-only), so readiness logic is unchanged.
        <div className="absolute inset-0 z-10">
          <FeedReelSkeleton />
        </div>
      )}
      <div
        className="w-full h-full relative z-[1]"
        style={{ visibility: isReady ? "visible" : "hidden" }}
      >
        <ReactPlayer
          key={source}
          ref={playerRef as RefObject<ReactPlayer>}
          url={url}
          width="100%"
          height="100%"
          playing={playing}
          muted={muted}
          // Always play inline (never auto-fullscreen on mobile web). This is
          // independent of `muted` — the FS listening prompt is unmuted yet must
          // stay inline in its 180×320 box.
          playsinline
          controls={false}
          playbackRate={playbackRate}
          onReady={() => setReadyKey(expectedKey)}
          onPlay={() => {
            actuallyPlayingRef.current = true;
            isPaused$.set(false);
          }}
          onPause={() => {
            actuallyPlayingRef.current = false;
            isPaused$.set(true);
          }}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onError={handleError}
          progressInterval={50}
          config={{
            youtube: {
              playerVars: {
                modestbranding: 1,
                rel: 0,
                showinfo: 0,
                // Hide video annotations and minimise the YouTube end/related
                // overlay so it can't cover the transcript (the clip also loops
                // before the natural end, so the "More videos" screen normally
                // never appears).
                iv_load_policy: 3,
                // Begin the YouTube player AT the clip offset so a deep-offset
                // clip (e.g. a Flight School prompt at ~54s into a 2-min video)
                // doesn't buffer the whole lead-in from 0 and then seek — the
                // YouTube analogue of the hls `startPosition` fix below. Whole
                // seconds only (YouTube's `start` is integer seconds). 0-offset
                // clips omit it (no-op).
                ...(lessonStartMs > 0
                  ? { start: Math.floor(lessonStartMs / 1000) }
                  : {}),
              },
            },
            // Pin a current hls.js (fast VOD far-seek) + begin loading AT the
            // clip start so a deep-offset clip fetches its segment first
            // (mirrors iOS). Single source of truth in @/lib/cloudflare so a
            // future hls.js pin bump stays in sync with CardVideoPlayer /
            // ActivityPlayer.
            file: cloudflareFileConfig(lessonStartMs),
          }}
        />
      </div>
      {/* Overlay blocks the iframe from stealing touch events (swipe nav) */}
      <div
        className="absolute inset-0 z-[5]"
        onClick={() => {
          // Drive off the player's REAL state, not the (possibly stale) isPaused$
          // flag: actually playing → pause; otherwise → play.
          if (actuallyPlayingRef.current) {
            isPaused$.set(true);
            return;
          }
          // Want to play. After a blocked autoplay, isPaused$ is already false
          // (we requested play, but no onPlay fired), so flipping it to false is
          // a no-op and the declarative `playing` prop never changes — the first
          // tap would do nothing. Kick the underlying player directly so this
          // user-gesture starts playback, then sync the flag.
          const internal = playerRef.current?.getInternalPlayer() as
            | { playVideo?: () => void; play?: () => void }
            | undefined;
          internal?.playVideo?.();
          internal?.play?.();
          isPaused$.set(false);
        }}
      />
    </div>
  );
});

export { VideoPlayer };
